import { useCallback, useEffect, useRef, useState } from "react";
import { OPTIMISTIC_TIMEOUT_MS, stimulusIsOn } from "../constants";
import type { Command, HeadsetSetting, HeadsetSettings, VrStateMsg } from "../types";

type Params = {
  vrState?: VrStateMsg;
  /** the base gate: a headset is selected and it is beating */
  canSend: boolean;
  sendCommand: (cmd: Command) => void;
};

type ToggleParams = {
  /** what vr.state says, or undefined while the field has not arrived */
  reported: boolean | undefined;
  /**
   * The vr.state message the `reported` value was read from. Only its identity is
   * used: the hook replaces the whole object on every vr.state, so a change here
   * means a fresh state has landed, whatever it happens to say.
   */
  stateToken: unknown;
  canSend: boolean;
  command: Command;
  sendCommand: (cmd: Command) => void;
};

/**
 * One switch backed by a *relative* command.
 *
 * Both settings the headset implements work this way: the command says "flip it",
 * not "set it to this", so the switch would sit on its old value until the next
 * state lands and would invite a second click. Show the intended value immediately,
 * then hand control back to the headset on the next vr.state.
 *
 * The override is dropped by whichever comes first:
 *   - a vr.state arriving after the command was sent — the headset's answer, taken
 *     as the truth whether or not it agrees with what we optimistically showed, so a
 *     refused or clamped command corrects on the spot instead of lingering;
 *   - the headset going quiet or being deselected, which means no answer is coming;
 *   - the timeout, for the case where it stays online and still never answers.
 *
 * The cost of syncing on the next state rather than on agreement: a vr.state the
 * headset emits for an unrelated reason (a step advancing) in the window between the
 * click and the command being handled will bounce the switch back for one message.
 */
function useRelativeToggle({
  reported,
  stateToken,
  canSend,
  command,
  sendCommand,
}: ToggleParams): HeadsetSetting {
  const [pending, setPending] = useState<boolean | null>(null);
  const timer = useRef<number | null>(null);
  /** the vr.state we were on when the command went out */
  const sentAgainst = useRef<unknown>(undefined);

  const on = pending ?? reported ?? false;

  const clearPending = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    setPending(null);
  }, []);

  useEffect(() => {
    if (pending === null) return;
    if (stateToken !== sentAgainst.current) clearPending();
  }, [stateToken, pending, clearPending]);

  // The headset stopped beating, or the selection moved off it. Either way no answer
  // is coming, and the switch is disabled from here on — showing the position we
  // optimistically hoped for, greyed out, for the rest of the timeout is a worse lie
  // than showing the last thing the headset actually said.
  useEffect(() => {
    if (!canSend && pending !== null) clearPending();
  }, [canSend, pending, clearPending]);

  useEffect(() => {
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  const toggle = useCallback(() => {
    if (!canSend) return;
    sentAgainst.current = stateToken;
    setPending((current) => !(current ?? reported ?? false));
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      // no vr.state came back at all — fall back to whatever we last heard
      setPending(null);
      timer.current = null;
    }, OPTIMISTIC_TIMEOUT_MS);
    sendCommand(command);
  }, [canSend, reported, stateToken, command, sendCommand]);

  return { on, enabled: canSend, toggle };
}

/**
 * A switch that may be turned on but not off again: once it shows the "on" value the
 * row goes dead, and only the headset itself can put it back.
 *
 * The lock reads the *displayed* position rather than the last reported one, so an
 * optimistic on lands dead immediately instead of staying live for the round trip and
 * inviting the very click it is meant to prevent. `toggle` is dropped rather than
 * only greyed out, so nothing can flip it while disabled.
 */
function lockWhenOn(setting: HeadsetSetting): HeadsetSetting {
  if (!setting.on) return setting;
  return { ...setting, enabled: false, toggle: () => {} };
}

/**
 * The two settings the headset actually implements.
 *
 * Language, audio instructions and short workflow are in the design but have no
 * field and no command behind them, so their rows are not rendered at all; see the
 * TODO in ../types.ts for what each of them needs before it can come back.
 */
export function useHeadsetSettings({
  vrState,
  canSend,
  sendCommand,
}: Params): HeadsetSettings {
  const subtitles = useRelativeToggle({
    reported: vrState?.subtitlesEnabled,
    stateToken: vrState,
    canSend,
    command: "toggle_subtitles",
    sendCommand,
  });

  // The switch is on for Robot and off for Bird. A headset that has not reported a
  // stimulus reads as undefined rather than false, so `on` still falls to Bird while
  // an optimistic value is not treated as confirmed by a state that says nothing.
  //
  // The swap only goes one way: Bird may be swapped to Robot, Robot may not be
  // swapped back from here. So the row is live on Bird and dead on Robot, and it
  // takes a reset — or the headset's own settings — to get back to Bird.
  const stimulus = lockWhenOn(
    useRelativeToggle({
      reported: stimulusIsOn(vrState?.stimulus),
      stateToken: vrState,
      canSend,
      command: "swap_stimulus",
      sendCommand,
    })
  );

  return { stimulus, subtitles };
}
